import { CSSProperties, FC, ReactNode, useState } from 'react';
import { Button, Checkbox, Divider, Dropdown, Menu, Space, Typography } from 'antd';
import {
    TbBraces,
    TbColumns3,
    TbFileTypeCsv,
    TbInfoCircle,
    TbPackageExport,
    TbSettings2,
    TbSquareCheckFilled,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    WORKSPACE_RESOURCE_LIST_COLUMN_OPTIONS,
    WorkspaceResourceListControls,
    WorkspaceResourceListSortKey,
} from '@/hooks/useWorkspaceResourceListControls';
import {
    DropdownButton,
    DropdownMenuLabel,
    DropdownMenuPanel,
    DropdownOptionContent,
} from '@/shared/components/dropdowns';
import ListLayoutOptionsDropdown from '@/shared/components/dropdowns/ListLayoutOptionsDropdown';
import { getCollapsibleHoverRevealStyle } from '@/shared/components/layouts/hoverReveal';
import { columnarListGridCellStyle, getColumnarListGridStyle } from '@/shared/components/lists/ColumnarListLayout';
import VirtualizedList from '@/shared/components/lists/VirtualizedList';
import { PlatformTableSortIcon } from '@/shared/components/tables/PlatformTable';
import { SmallTag } from '@/shared/components/tags';

import { WORKSPACE_RESOURCE_LIST_ACTIONS_WIDTH_REM } from './Skeleton';

export { default as WorkspaceResourceListBulkActions } from './BulkActions';
export { WorkspaceResourceListDescription, WorkspaceResourceListStatusTag } from './Presentation';
export { default as WorkspaceResourceListSkeleton } from './Skeleton';
export { default as WorkspaceResourceCollectionSkeleton } from './CollectionSkeleton';
export { default as WorkspaceResourceListBody } from './Body';
export { default as WorkspaceResourceListPageHeader } from './PageHeader';

interface SortButtonProps {
    controls: WorkspaceResourceListControls;
    disabled: boolean;
    label: string;
    sortKey: WorkspaceResourceListSortKey;
}

const SortButton: FC<SortButtonProps> = ({ controls, disabled, label, sortKey }) => {
    const active = controls.sortState?.key === sortKey;

    return (
        <Button
            aria-label={`Sort by ${label}`}
            disabled={disabled}
            icon={<PlatformTableSortIcon direction={active ? controls.sortState?.direction : undefined} />}
            onClick={() => controls.toggleColumnSort(sortKey)}
            size='small'
            type='text'
        />
    );
};

interface WorkspaceResourceListHeaderProps {
    controls: WorkspaceResourceListControls;
    disabled?: boolean;
    label: string;
}

export const WorkspaceResourceListHeader: FC<WorkspaceResourceListHeaderProps> = ({
    controls,
    disabled = false,
    label,
}) => {
    const { token } = useAntDesignTheme();
    const globalSortOption = controls.sortState?.key === 'name' ? 'alphabetical' : controls.sortState?.key;
    const exportDisabled = disabled || controls.itemCount === 0;
    const gridStyle = getColumnarListGridStyle({
        actionColumnWidthRem: WORKSPACE_RESOURCE_LIST_ACTIONS_WIDTH_REM,
        columnCount: controls.columns.length,
    });

    return (
        <div
            data-workspace-resource-list-header-section='true'
            style={{
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                boxSizing: 'border-box',
                flex: 'none',
                overflowX: 'auto',
                padding: '0.75rem 1rem',
            }}
        >
            <div
                data-workspace-resource-list-header-grid='true'
                style={{
                    ...gridStyle,
                    alignItems: 'center',
                    backgroundColor: token.colorBgBase,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    boxSizing: 'border-box',
                    padding: '0.25rem',
                }}
            >
                <div style={{ ...columnarListGridCellStyle, alignItems: 'center', display: 'flex' }}>
                    <span data-selection-control='true' style={{ display: 'inline-flex', marginLeft: '0.25rem' }}>
                        <Checkbox
                            aria-label={`Select all ${label.toLowerCase()}`}
                            checked={controls.allSelected}
                            disabled={disabled}
                            indeterminate={controls.indeterminate}
                            onChange={(event) => controls.toggleAll(event.target.checked)}
                        />
                    </span>
                    <Divider type='vertical' />
                    <span
                        style={{
                            alignItems: 'center',
                            color: token.colorTextTertiary,
                            display: 'inline-flex',
                            gap: '0.375rem',
                            minWidth: 0,
                            paddingInline: '0.25rem',
                        }}
                    >
                        <TbInfoCircle size='0.875rem' />
                        <Typography.Text ellipsis type='secondary'>
                            {label}
                        </Typography.Text>
                    </span>
                    <SortButton controls={controls} disabled={disabled} label='Name' sortKey='name' />
                    {controls.selectedCount > 0 ? (
                        <SmallTag
                            bordered={false}
                            message={controls.selectedCount}
                            styles={{ marginLeft: '0.25rem' }}
                        />
                    ) : null}
                </div>

                {controls.columns.map((columnKey, index) => {
                    const column = WORKSPACE_RESOURCE_LIST_COLUMN_OPTIONS.find(({ key }) => key === columnKey);

                    return (
                        <div
                            key={`${columnKey}-${index}`}
                            style={{
                                ...columnarListGridCellStyle,
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Space.Compact size='small'>
                                <Dropdown
                                    disabled={disabled}
                                    placement='bottomLeft'
                                    trigger={['click']}
                                    popupRender={() => (
                                        <DropdownMenuPanel selectedKeys={[columnKey]} style={{ minWidth: 240 }}>
                                            {WORKSPACE_RESOURCE_LIST_COLUMN_OPTIONS.map((option) => (
                                                <Menu.Item
                                                    key={option.key}
                                                    onClick={() => controls.setColumnAt(index, option.key)}
                                                >
                                                    <DropdownOptionContent
                                                        selected={option.key === columnKey}
                                                        title={option.label}
                                                    />
                                                </Menu.Item>
                                            ))}
                                        </DropdownMenuPanel>
                                    )}
                                >
                                    <DropdownButton
                                        disabled={disabled}
                                        size='small'
                                        style={{ maxWidth: '8rem' }}
                                        type='text'
                                    >
                                        <Typography.Text ellipsis>{column?.label}</Typography.Text>
                                    </DropdownButton>
                                </Dropdown>
                                {column?.sortable ? (
                                    <SortButton
                                        controls={controls}
                                        disabled={disabled}
                                        label={column.label}
                                        sortKey={columnKey}
                                    />
                                ) : null}
                            </Space.Compact>
                        </div>
                    );
                })}

                <div
                    data-workspace-resource-list-utilities='true'
                    style={{
                        ...columnarListGridCellStyle,
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        columnGap: '1rem',
                        display: 'grid',
                        gridTemplateColumns: '1px minmax(0, 1fr)',
                    }}
                >
                    <span
                        aria-hidden='true'
                        data-workspace-resource-list-utilities-divider='true'
                        style={{
                            alignSelf: 'center',
                            backgroundColor: token.colorBorder,
                            height: '1rem',
                            width: '1px',
                        }}
                    />
                    <Space.Compact size='small' style={{ justifySelf: 'end' }}>
                        <Dropdown
                            disabled={exportDisabled}
                            placement='bottomRight'
                            trigger={['click']}
                            popupRender={() => (
                                <DropdownMenuPanel style={{ minWidth: 248 }}>
                                    <DropdownMenuLabel>Export Current View</DropdownMenuLabel>
                                    <Menu.Divider />
                                    <Menu.Item
                                        icon={<TbFileTypeCsv size='1rem' />}
                                        key='csv'
                                        onClick={() => controls.exportCurrentView('csv', label)}
                                    >
                                        Export to CSV
                                    </Menu.Item>
                                    <Menu.Item
                                        icon={<TbBraces size='1rem' />}
                                        key='json'
                                        onClick={() => controls.exportCurrentView('json', label)}
                                    >
                                        Export to JSON
                                    </Menu.Item>
                                </DropdownMenuPanel>
                            )}
                        >
                            <DropdownButton
                                aria-label={`Export ${label.toLowerCase()}`}
                                disabled={exportDisabled}
                                size='small'
                                trailingIcon={null}
                            >
                                <TbPackageExport size='0.875rem' style={{ color: token.colorTextTertiary }} />
                            </DropdownButton>
                        </Dropdown>
                        <ListLayoutOptionsDropdown
                            compactTrigger
                            compactTriggerType='default'
                            disabled={disabled}
                            onOptionChange={controls.onGlobalSortChange}
                            option={globalSortOption}
                            route=''
                            showKeyboardShortcutLabels={false}
                        />
                        <Dropdown
                            disabled={disabled}
                            placement='bottomRight'
                            trigger={['click']}
                            popupRender={() => (
                                <DropdownMenuPanel style={{ minWidth: 248 }}>
                                    <DropdownMenuLabel>List Options</DropdownMenuLabel>
                                    <Menu.Divider />
                                    <Menu.Item
                                        icon={<TbColumns3 size='1rem' />}
                                        key='reset-columns'
                                        onClick={controls.resetColumns}
                                    >
                                        Reset Columns
                                    </Menu.Item>
                                    <Menu.Item
                                        disabled={controls.selectedCount === 0}
                                        icon={<TbSquareCheckFilled size='1rem' />}
                                        key='clear-selection'
                                        onClick={() => controls.toggleAll(false)}
                                    >
                                        Clear Selection
                                    </Menu.Item>
                                </DropdownMenuPanel>
                            )}
                        >
                            <Button
                                aria-label={`${label} list options`}
                                disabled={disabled}
                                icon={<TbSettings2 size='0.875rem' style={{ color: token.colorTextTertiary }} />}
                                size='small'
                            />
                        </Dropdown>
                    </Space.Compact>
                </div>
            </div>
        </div>
    );
};

interface WorkspaceResourceListItemProps {
    children: (selectionControl: ReactNode) => ReactNode;
    controls?: WorkspaceResourceListControls;
    inset?: boolean;
    resourceId: string;
    resourceLabel: string;
    style?: CSSProperties;
}

/** Preserves each resource's established row while adding the platform-standard compact selection reveal. */
export const WorkspaceResourceListItem: FC<WorkspaceResourceListItemProps> = ({
    children,
    controls,
    inset = Boolean(controls),
    resourceId,
    resourceLabel,
    style,
}) => {
    const { token } = useAntDesignTheme();
    const [hovered, setHovered] = useState(false);
    const [focusedWithin, setFocusedWithin] = useState(false);
    const checked = controls?.isSelected(resourceId) ?? false;
    const selectionVisible = Boolean(controls && (hovered || focusedWithin || checked));
    const selectionControl = controls ? (
        <span
            data-workspace-resource-list-selection='true'
            style={getCollapsibleHoverRevealStyle(
                selectionVisible,
                token,
                { expandedMaxWidth: '1rem', gap: '0.5rem', side: 'leading' },
                { alignItems: 'center' },
            )}
        >
            <Checkbox
                aria-label={`${checked ? 'Deselect' : 'Select'} ${resourceLabel}`}
                checked={checked}
                onChange={(event) => controls.toggleSelected(resourceId, event.target.checked)}
                onClick={(event) => event.stopPropagation()}
            />
        </span>
    ) : null;
    const content = children(selectionControl);

    return (
        <VirtualizedList.Item
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    setFocusedWithin(false);
                }
            }}
            onFocus={() => setFocusedWithin(true)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{ paddingInline: inset ? '1.5rem' : undefined, ...style }}
        >
            {controls ? (
                <div
                    data-workspace-resource-list-grid='true'
                    style={getColumnarListGridStyle({
                        actionColumnWidthRem: WORKSPACE_RESOURCE_LIST_ACTIONS_WIDTH_REM,
                        columnCount: controls.columns.length,
                    })}
                >
                    {content}
                </div>
            ) : (
                content
            )}
        </VirtualizedList.Item>
    );
};

interface WorkspaceResourceListCellProps {
    children?: ReactNode;
    style?: CSSProperties;
}

export const WorkspaceResourceListCell: FC<WorkspaceResourceListCellProps> = ({ children, style }) => (
    <span
        style={{
            ...columnarListGridCellStyle,
            overflow: 'hidden',
            textAlign: 'right',
            textOverflow: 'ellipsis',
            ...style,
        }}
    >
        {children}
    </span>
);

interface WorkspaceResourceListColumnsLayoutProps {
    aligned?: boolean;
    children: ReactNode;
}

/** Places dynamic values and row actions on the shared Search-style grid tracks. */
export const WorkspaceResourceListColumnsLayout: FC<WorkspaceResourceListColumnsLayoutProps> = ({ children }) => (
    <div data-workspace-resource-list-columns='true' style={{ display: 'contents' }}>
        {children}
    </div>
);

interface WorkspaceResourceListLeadingControlsProps {
    avatar: ReactNode;
    favoriteControl: ReactNode;
    selectionControl: ReactNode;
}

/** Gives the optional checkbox, favorite control, and avatar one consistent half-rem rhythm. */
export const WorkspaceResourceListLeadingControls: FC<WorkspaceResourceListLeadingControlsProps> = ({
    avatar,
    favoriteControl,
    selectionControl,
}) => (
    <div style={{ alignItems: 'center', display: 'flex', marginTop: '0.25rem' }}>
        {selectionControl}
        <span style={{ display: 'inline-flex', marginInlineEnd: '0.5rem' }}>{favoriteControl}</span>
        {avatar}
    </div>
);

interface WorkspaceResourceListActionsCellProps {
    aligned?: boolean;
    children: ReactNode;
}

export const WorkspaceResourceListActionsCell: FC<WorkspaceResourceListActionsCellProps> = ({
    aligned = true,
    children,
}) => (
    <span
        style={
            aligned
                ? {
                      alignItems: 'center',
                      display: 'inline-flex',
                      justifyContent: 'flex-end',
                      minWidth: 0,
                  }
                : { display: 'contents' }
        }
    >
        {children}
    </span>
);
